import {Module, ModuleItem} from '@dms/api/shared';
import {moduleKeys} from '@dms/config';
import featureFlags from '@dms/feature-flags';
import {workshopRoutes} from '@dms/routes';
import {permissions, useImporterReportingIntegrations} from '@dms/shared';

import {buildArray} from 'shared/web';

import {CreateServiceCase} from './pages/CreateServiceCase/CreateServiceCase';
import {MercedesMraReport} from './pages/MercedesMraReport/MercedesMraReport';
import {PostponedRequests} from './pages/PostponedRequests/PostponedRequests';
import {Report5starCstar} from './pages/Report5starCstar/Report5starCstar';
import {ServiceCampaignDetail} from './pages/ServiceCampaignDetail/ServiceCampaignDetail';
import {ServiceCampaignsOverview} from './pages/ServiceCampaigns/ServiceCampaignsOverview';
import {ServiceCaseDetail} from './pages/ServiceCaseDetail/ServiceCaseDetail';
import {WorkshopInterest} from './pages/WorkshopInterest/WorkshopInterest';
import {WorkshopInterestCreate} from './pages/WorkshopInterestCreate/WorkshopInterestCreate';
import {WorkshopInterestDetail} from './pages/WorkshopInterestDetail/WorkshopInterestDetail';
import {WorkshopOverview} from './pages/WorkshopOverview/WorkshopOverview';
import {WorkshopReport} from './pages/WorkshopReport/WorkshopReport';

export function useWorkshopModule(): Module {
  const importerReportingIntegrations = useImporterReportingIntegrations();

  const content = buildArray<ModuleItem>([
    {
      id: 'workshop-serviceCases',
      routerProps: {
        path: workshopRoutes.serviceCases,
        element: <WorkshopOverview />,
      },
      layoutProps: {
        title: 'page.workshop.labels.serviceCases',
        icon: 'sidebar/workshop_service_cases',
      },
      requiredPermission: permissions.serviceCaseRead,
    },
    {
      id: 'workshop-serviceOrders',
      routerProps: {
        path: workshopRoutes.serviceOrders,
        element: <WorkshopOverview />,
      },
      layoutProps: {
        title: 'page.workshop.labels.serviceOrders',
        icon: 'sidebar/workshop_service_orders',
      },
      requiredPermission: permissions.serviceCaseRead,
    },
    {
      id: 'workshop-directSales',
      routerProps: {
        path: workshopRoutes.directSales,
        element: <WorkshopOverview />,
      },
      layoutProps: {
        title: 'page.workshop.labels.directSales',
        icon: 'sidebar/workshop_direct_sales',
      },
      requiredPermission: permissions.serviceCaseRead,
    },
    {
      id: 'workshop-labour-operation-settings',
      routerProps: {
        path: workshopRoutes.labourOperationSettings,
        element: <WorkshopOverview />,
      },
      layoutProps: {
        title: 'page.workshop.labels.labourOperationSettings',
        icon: 'sidebar/workshop_labour_operation_settings',
      },
      requiredPermission: permissions.readLabourCatalogue,
    },
    {
      id: 'workshop-interest',
      routerProps: {
        path: workshopRoutes.interest,
        element: <WorkshopInterest />,
      },
      requiredPermission: permissions.workshopInterestRead,
      requiredFeatureFlag: featureFlags.WORKSHOP_INTEREST,
    },
    {
      id: 'workshop-interestCreate',
      routerProps: {
        path: workshopRoutes.interestCreate,
        element: <WorkshopInterestCreate />,
      },
      requiredPermission: permissions.workshopInterestCreate,
      requiredFeatureFlag: featureFlags.WORKSHOP_INTEREST,
    },
    {
      id: 'workshop-interestDetail',
      routerProps: {
        paths: [
          workshopRoutes.interestDetailOverview,
          workshopRoutes.interestDetailCustomer,
          workshopRoutes.interestDetailOwnership,
          workshopRoutes.interestDetailServiceRequest,
          workshopRoutes.interestDetailChecklist,
          workshopRoutes.interestDetailTasks,
          workshopRoutes.interestDetailActionLog,
        ],
        element: <WorkshopInterestDetail />,
      },
      requiredPermission: permissions.workshopInterestRead,
      requiredFeatureFlag: featureFlags.WORKSHOP_INTEREST,
    },
    {
      id: 'workshop-draftServiceCase',
      routerProps: {
        path: workshopRoutes.draftServiceCase,
        element: <CreateServiceCase />,
      },
      requiredPermission: permissions.serviceCaseRead,
    },
    {
      id: 'workshop-createServiceCase',
      routerProps: {
        path: workshopRoutes.createServiceCase,
        element: <CreateServiceCase />,
      },
      requiredPermission: permissions.serviceCaseRead,
      layoutProps: {
        title: 'page.workshop.labels.newServiceCase',
        isPartOfCreateMenuItem: true,
      },
    },
    {
      id: 'workshop-serviceCase-detail',
      routerProps: {
        path: workshopRoutes.serviceCaseDetail,
        element: <ServiceCaseDetail />,
      },
      requiredPermission: permissions.serviceCaseRead,
    },
    {
      id: 'workshop-service-campaigns',
      routerProps: {
        path: workshopRoutes.serviceCampaigns,
        element: <ServiceCampaignsOverview />,
      },
      layoutProps: {
        title: 'page.workshop.labels.serviceCampaigns',
        icon: 'sidebar/workshop_service_campaigns',
      },
      requiredPermission: permissions.serviceCaseRead,
    },
    {
      id: 'workshop-service-campaigns-Hyundai',
      routerProps: {
        path: workshopRoutes.serviceCampaignsUnfinished,
        element: <ServiceCampaignsOverview />,
      },
      requiredPermission: permissions.serviceCaseRead,
    },
    {
      id: 'workshop-service-campaign-detail',
      routerProps: {
        path: workshopRoutes.serviceCampaignDetail,
        element: <ServiceCampaignDetail />,
      },
      requiredPermission: permissions.serviceCaseRead,
    },
    {
      id: 'workshop-postponed-requests',
      routerProps: {
        path: workshopRoutes.postponedRequests,
        element: <PostponedRequests />,
      },
      layoutProps: {
        title: 'page.workshop.labels.postponedRequests',
        icon: 'sidebar/workshop_postponed_requests',
      },
      requiredPermission: permissions.serviceCaseRead,
    },
    {
      id: 'workshop-reporting',
      routerProps: {
        path: workshopRoutes.reporting,
        element: <WorkshopReport />,
      },
      layoutProps: {
        title: 'analytics.label.workshopReports',
        icon: 'sidebar/analytics',
      },
      requiredPermission: permissions.serviceCaseRead,
    },
    {
      id: 'workshop-reporting-tab',
      routerProps: {
        path: workshopRoutes.reportingTab,
        element: <WorkshopReport />,
      },
      requiredPermission: permissions.serviceCaseRead,
    },
  ])
    .when(
      importerReportingIntegrations.isMercedesMraEnabled,
      {
        id: 'workshop-reporting-mercedes-mra',
        routerProps: {
          path: workshopRoutes.reportingMercedesMra,
          element: <MercedesMraReport />,
        },
        layoutProps: {
          title: 'analytics.label.mercedesMra',
          icon: 'sidebar/analytics',
        },
        requiredPermission: permissions.serviceCaseRead,
      },
      {
        id: 'workshop-reporting-mercedes-mra-custom-reports',
        routerProps: {
          path: workshopRoutes.reportingMercedesMraCustomReports,
          element: <MercedesMraReport />,
        },
        requiredPermission: permissions.serviceCaseRead,
      },
      {
        id: 'workshop-reporting-mercedes-mra-inactive-reports',
        routerProps: {
          path: workshopRoutes.reportingMercedesMraInactiveReports,
          element: <MercedesMraReport />,
        },
        requiredPermission: permissions.serviceCaseRead,
      }
    )
    .when(importerReportingIntegrations.isMerc5StarEnabled, {
      id: 'workshop-reporting-5star',
      routerProps: {
        path: workshopRoutes.reportingFiveStar,
        element: <Report5starCstar type="5star" />,
      },
      layoutProps: {
        title: 'analytics.label.5starReports',
        icon: 'sidebar/analytics',
      },
      requiredPermission: permissions.serviceCaseRead,
    })
    .when(importerReportingIntegrations.isMercCstarEnabled, {
      id: 'workshop-reporting-cstar',
      routerProps: {
        path: workshopRoutes.reportingCStar,
        element: <Report5starCstar type="cstar" />,
      },
      layoutProps: {
        title: 'analytics.label.CstarReports',
        icon: 'sidebar/analytics',
      },
      requiredPermission: permissions.serviceCaseRead,
    });

  return {
    id: moduleKeys.workshop,
    layoutProps: {
      title: 'module.workshop.title',
      icon: 'sidebar/workshop',
    },
    routerProps: {
      path: workshopRoutes.overview,
      element: <WorkshopOverview />,
    },
    requiredPermissionAtLeastOne: [
      permissions.serviceCaseRead,
      permissions.readLabourCatalogue,
      permissions.workshopInterestRead,
    ],
    content,
  };
}
